import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PageContent,
  Container,
  Button,
  Modal,
  FormActions,
  FormSection,
  FormSummary,
  FormSummaryHeading,
  FormSummaryMeta,
  StatusBadge,
} from 'components';
import { formatDateTime } from 'helpers/date';
import {
  loadIpmaMeetingAttendanceApplicationCreator,
  decideIpmaMeetingApplicationCreator,
  removeIpmaMeetingAttendanceCreator,
} from 'store/actionsCreators';
import { describeSubmitter } from 'containers/ma/Meetings/Application/submitter';
import { SummaryList } from 'containers/ma/Meetings/Application/Application.styled';
import StatusHistory from 'containers/ma/Meetings/Application/StatusHistory';
import { ReasonInput, ReasonLabel } from '../Application/Application.styled';
import AttendanceQuestions from './AttendanceQuestions';

/**
 * The reason of a decision. Uncontrolled for the same reason as on the meeting
 * applications: the modal rebuilds itself on every render of the screen, so a
 * controlled field would lose the focus after each keystroke.
 */
const ReasonField = ({ label, placeholder, onChange }) => (
  <>
    <ReasonLabel>{label}</ReasonLabel>
    <ReasonInput
      rows={4}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  </>
);

ReasonField.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * One attendance form somebody sent, read only, with the accept / reject
 * decision the people holding the attendance rights make on it.
 *
 * The questions are rebuilt from the meetings the form itself answered rather
 * than from the ones open right now, so a decision made after a meeting closed
 * still shows what was actually filled in.
 */
const AttendanceApplication = ({ data, loadApplication, decideApplication, clearApplication }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { applicationId } = useParams();

  const [decision, setDecision] = useState(null);
  const reason = useRef('');

  useEffect(() => {
    loadApplication({ applicationId });

    return () => clearApplication();
  }, [applicationId]);

  if (!data || !data.application) {
    return <PageContent />;
  }

  const { application, questions, dinner, answers, applicant } = data;
  const backPath = '/ipma/meetings/attendance';

  const confirmDecision = () => {
    decideApplication(
      {
        applicationId: application.id,
        meetingType: 'attendance',
        accept: decision,
        reason: reason.current,
      },
      history,
    );

    setDecision(null);
  };

  return (
    <PageContent>
      <Container>
        <FormSummary>
          <div>
            <FormSummaryHeading>{t('page.ipma.meetings.attendance.title')}</FormSummaryHeading>
            <FormSummaryMeta>{t('page.ipma.meetings.attendance.description')}</FormSummaryMeta>
          </div>
          <StatusBadge status={application.status}>{application.statusLabel}</StatusBadge>
        </FormSummary>

        <FormSection title={t('page.ipma.meetings.application.applicantSection')}>
          <SummaryList>
            <dt>{t('page.ipma.meetings.application.applicant')}</dt>
            <dd>{applicant ? `${applicant.name} (${applicant.email})` : '-'}</dd>
            <dt>{t('page.ipma.meetings.application.submittedBy')}</dt>
            <dd>{describeSubmitter(data.submittedBy) || '-'}</dd>
            <dt>{t('page.ipma.meetings.application.submittedAt')}</dt>
            <dd>{formatDateTime(application.submittedAt) || '-'}</dd>
          </SummaryList>
        </FormSection>

        <AttendanceQuestions questions={questions} dinner={dinner} answers={answers} disabled />

        <FormActions>
          {data.decidable && (
            <>
              <Button type="button" style={{ width: 'auto' }} onClick={() => setDecision(true)}>
                {t('page.ipma.meetings.application.accept')}
              </Button>
              <Button
                type="button"
                secondary
                style={{ width: 'auto' }}
                onClick={() => setDecision(false)}
              >
                {t('page.ipma.meetings.application.reject')}
              </Button>
            </>
          )}
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => history.push(backPath)}
          >
            {t('page.ipma.meetings.attendance.back')}
          </Button>
        </FormActions>

        <StatusHistory items={data.statusHistory} />
      </Container>

      <Modal
        heading={
          decision
            ? t('page.ipma.meetings.application.confirmAccept')
            : t('page.ipma.meetings.application.confirmReject')
        }
        description={
          <ReasonField
            label={t('page.ipma.meetings.application.reasonLabel')}
            placeholder={t('page.ipma.meetings.attendance.reasonPlaceholder')}
            onChange={(value) => {
              reason.current = value;
            }}
          />
        }
        isOpen={decision !== null}
        confirm={confirmDecision}
        cancel={() => setDecision(null)}
        onRequestClose={() => setDecision(null)}
      />
    </PageContent>
  );
};

AttendanceApplication.propTypes = {
  data: PropTypes.shape({
    questions: PropTypes.arrayOf(PropTypes.shape({})),
    dinner: PropTypes.bool,
    answers: PropTypes.shape({}),
    application: PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.string,
      statusLabel: PropTypes.string,
      submittedAt: PropTypes.string,
    }),
    applicant: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    }),
    decidable: PropTypes.bool,
    statusHistory: PropTypes.arrayOf(PropTypes.shape({})),
    submittedBy: PropTypes.shape({
      name: PropTypes.string,
      organization: PropTypes.string,
      role: PropTypes.string,
    }),
  }),
  loadApplication: PropTypes.func.isRequired,
  decideApplication: PropTypes.func.isRequired,
  clearApplication: PropTypes.func.isRequired,
};

AttendanceApplication.defaultProps = {
  data: null,
};

const mapStateToProps = ({ ipma }) => ({
  data: ipma.meetings.attendanceForm.data,
});

const mapDispatchToProps = (dispatch) => ({
  loadApplication: (payload) => dispatch(loadIpmaMeetingAttendanceApplicationCreator(payload)),
  decideApplication: (payload, history) =>
    dispatch(decideIpmaMeetingApplicationCreator(payload, history)),
  clearApplication: () => dispatch(removeIpmaMeetingAttendanceCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AttendanceApplication);
